import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Award, Mail, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [debugToken, setDebugToken] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSubmitted(true);
      // In development, show the debug token
      if (response.data.debug_token) {
        setDebugToken(response.data.debug_token);
      }
      toast.success('If the email exists, a reset link has been sent');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <main className="min-h-screen pt-16 flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-green-500/10 flex items-center justify-center">
            <Mail className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="font-['Playfair_Display'] text-2xl font-semibold text-white mb-4">
            Check Your Email
          </h1>
          <p className="text-white/60 mb-6">
            If an account exists with that email, we've sent password reset instructions.
          </p>
          
          {/* Debug info for development - shows the reset token */}
          {debugToken && (
            <div className="mb-6 p-4 bg-[#121212] rounded-lg border border-[#D4AF37]/30">
              <p className="text-[#D4AF37] text-sm mb-2 font-semibold">Development Mode:</p>
              <p className="text-white/60 text-xs mb-2">Reset link (check console for full details):</p>
              <Link 
                to={`/reset-password?token=${debugToken}`}
                className="text-[#D4AF37] text-sm hover:underline break-all"
              >
                Click here to reset password
              </Link>
            </div>
          )}
          
          <Link to="/login">
            <Button variant="outline" className="border-white/10 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Button>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen pt-16 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8960F] flex items-center justify-center">
              <Award className="w-7 h-7 text-[#0A0A0A]" />
            </div>
          </Link>
          <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-white mb-2">
            Forgot Password
          </h1>
          <p className="text-white/60">
            Enter your email to receive reset instructions
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-[#121212] rounded-2xl border border-white/5 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="pl-12 h-12 bg-[#0A0A0A] border-white/10 text-white placeholder:text-white/30"
                  data-testid="forgot-email"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#D4AF37] hover:bg-[#E5C048] text-[#0A0A0A] font-semibold"
              data-testid="forgot-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-[#0A0A0A] border-t-transparent rounded-full animate-spin" />
                  Sending...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Send Reset Link
                  <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-white/50 hover:text-white flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </main>
  );
}
